import React, { useEffect, useMemo, useState } from 'react';
import { Checkbox } from '@progress/kendo-react-inputs';
import { Label } from '@progress/kendo-react-labels';
import { useLocalization } from '@progress/kendo-react-intl';
import { enMessages } from '../../../../../messages/en-US';
import type { EntityNoteCategory } from './EntityNotesPanel';
import { NOTE_CATEGORY_KEYS, NOTE_CATEGORY_MESSAGE_KEY } from './noteCategories';

export type CategoryOption = { value: EntityNoteCategory; text: string };

/**
 * Localized note categories + helpers, shared by every notes surface
 * (per-asset tab, dashboard-wide tab, entity panel).
 *
 * Returns the localized `categories` list, a `categoryLabel(cat)` lookup, and the
 * `renderCategoryItem` checkbox renderer used inside the category `MultiSelect`.
 */
export function useNoteCategories() {
  const localizationService = useLocalization();

  const categories: CategoryOption[] = useMemo(
    () =>
      NOTE_CATEGORY_KEYS.map((value) => ({
        value,
        text: localizationService.toLanguageString(
          `custom.${NOTE_CATEGORY_MESSAGE_KEY[value]}`,
          (enMessages.custom as Record<string, string>)[NOTE_CATEGORY_MESSAGE_KEY[value]],
        ),
      })),
    [localizationService],
  );

  const categoryLabel = (cat: EntityNoteCategory) => categories.find((c) => c.value === cat)?.text ?? cat;

  /** Kendo Checkbox + Label per category MultiSelect item. */
  const renderCategoryItem = (li: React.ReactElement, itemProps: any) => {
    const inner = (
      <span>
        <Checkbox
          name={itemProps.dataItem.value}
          checked={itemProps.selected}
          onChange={() => {
            const syntheticEvent = {
              stopPropagation: () => {},
              preventDefault: () => {},
              target: { checked: !itemProps.selected },
            };
            itemProps.onClick(itemProps.index, syntheticEvent);
          }}
        >
          <Label className="k-checkbox-label">&nbsp;{itemProps.dataItem.text}</Label>
        </Checkbox>
      </span>
    );
    return React.cloneElement(li, li.props, inner);
  };

  return { categories, categoryLabel, renderCategoryItem };
}

export interface UseNoteEditingOptions {
  /** Localized category options (from `useNoteCategories`) — used to seed the edit MultiSelect. */
  categories: CategoryOption[];
  /** Current notes in view — when the edited note disappears (deleted/filtered out), edit state clears. */
  notes: { id: string }[];
  /** Persist an edit (own notes only). */
  onSave: (
    id: string,
    value: { description: string; categories: EntityNoteCategory[]; isInternalOnly: boolean },
  ) => void;
  /** Delete a note (own notes only). */
  onDelete: (id: string) => void;
}

/**
 * Inline edit/delete state machine for a notes timeline. Identical behaviour across the
 * per-asset tab, the dashboard-wide tab, and the entity panel — only `onSave`/`onDelete` differ
 * (local state vs React Query mutations).
 */
export function useNoteEditing({ categories, notes, onSave, onDelete }: UseNoteEditingOptions) {
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingDescription, setEditingDescription] = useState('');
  const [editingCategories, setEditingCategories] = useState<CategoryOption[]>([]);
  const [editingInternalOnly, setEditingInternalOnly] = useState(false);

  const canSaveEdit = editingDescription.trim().length > 0 && editingCategories.length > 0;

  const startEdit = (note: {
    id: string;
    description: string;
    categories: EntityNoteCategory[];
    isInternalOnly?: boolean;
  }) => {
    setEditingId(note.id);
    setEditingDescription(note.description);
    setEditingCategories(categories.filter((c) => note.categories.includes(c.value)));
    setEditingInternalOnly(note.isInternalOnly ?? false);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditingDescription('');
    setEditingCategories([]);
    setEditingInternalOnly(false);
  };

  const saveEdit = () => {
    if (!canSaveEdit || !editingId) return;
    onSave(editingId, {
      description: editingDescription.trim(),
      categories: editingCategories.map((c) => c.value),
      isInternalOnly: editingInternalOnly,
    });
    cancelEdit();
  };

  const deleteNote = (id: string) => {
    onDelete(id);
    if (editingId === id) cancelEdit();
  };

  // If the note being edited drops out of the list, clear the edit state.
  useEffect(() => {
    if (editingId && !notes.some((n) => n.id === editingId)) cancelEdit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notes]);

  return {
    editingId,
    editingDescription,
    setEditingDescription,
    editingCategories,
    setEditingCategories,
    editingInternalOnly,
    setEditingInternalOnly,
    canSaveEdit,
    startEdit,
    cancelEdit,
    saveEdit,
    deleteNote,
  };
}
